class Disclosure extends HTMLElement {
  static instances = [];
  static resizeObserver;

  constructor() {
    super();
    this.boundUpdateFormPosition = this.updateFormPosition.bind(this);
  }

  connectedCallback() {
    this.viewport = this.closest(".drawer--instance, .layout--main-content");
    Disclosure.instances.push(this);

    // Cache DOM elements once
    this.current_option = this.querySelector(".disclosure--current-option");
    this.form = this.querySelector(".disclosure--form");
    this.input = this.querySelector('[data-item="disclosure"]');
    this.links = this.querySelectorAll(".disclosure--option");
    this.toggle = this.querySelector(".disclosure--toggle");
    this.type = this.getAttribute("data-type");
    this.toggle_and_form_gap = 8;
    this.window_and_form_gap = 16;

    if (document.readyState === "loading") {
      window.addEventListener("DOMContentLoaded", this.init.bind(this), { once: true });
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    // Remove event listeners
    this.removeEventListeners();
    
    // Remove from instances array
    const index = Disclosure.instances.indexOf(this);
    if (index > -1) {
      Disclosure.instances.splice(index, 1);
    }
  }

  init() {
    this.addEventListeners();
    this.updateFormPosition();
    
    if (this.type !== "option-picker") {
      this.optionChangeCallback();
    }
  }

  addEventListeners() {
    // Use shared resize observer
    if (!Disclosure.resizeObserver) {
      Disclosure.resizeObserver = new ResizeObserver(() => {
        Disclosure.instances.forEach(instance => instance.updateFormPosition());
      });
      Disclosure.resizeObserver.observe(document.body);
    }

    // Drawer listeners
    if (this.closest('.drawer--instance[data-side="left"]')) {
      window.addEventListener("theme:drawer:left:opened", this.boundUpdateFormPosition);
    }
    if (this.closest('.drawer--instance[data-side="right"]')) {
      window.addEventListener("theme:drawer:right:opened", this.boundUpdateFormPosition);
    }

    // Toggle listeners
    this.toggle.addEventListener("click", this.handleToggleClick.bind(this));
    this.toggle.addEventListener("keydown", this.handleToggleKeydown.bind(this));

    // Focus management
    this.addEventListener("focusout", this.handleFocusOut.bind(this));
    this.addEventListener("keydown", this.handleKeydown.bind(this));

    // Link listeners - use event delegation
    this.form.addEventListener("click", this.handleLinkClick.bind(this));
    this.form.addEventListener("keydown", this.handleLinkKeydown.bind(this));
  }

  removeEventListeners() {
    // Clean up all event listeners
    if (this.closest('.drawer--instance[data-side="left"]')) {
      window.removeEventListener("theme:drawer:left:opened", this.boundUpdateFormPosition);
    }
    if (this.closest('.drawer--instance[data-side="right"]')) {
      window.removeEventListener("theme:drawer:right:opened", this.boundUpdateFormPosition);
    }
    
    // We don't need to remove the delegated event listeners as they'll be garbage collected
  }

  handleToggleClick(event) {
    event.preventDefault();
    const expanded = this.toggle.getAttribute("aria-expanded") === "true";
    this.toggleDisclosure(!expanded);
  }

  handleToggleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      const expanded = this.toggle.getAttribute("aria-expanded") === "true";
      this.toggleDisclosure(!expanded);
    }
  }

  handleFocusOut(event) {
    if (!this.contains(event.relatedTarget)) {
      this.toggleFormDisplay(false);
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.toggleFormDisplay(false);
      this.toggle.focus();
    }
  }

  handleLinkClick(event) {
    const target = event.target.closest(".disclosure--option");
    if (!target) return;
    
    this.selectOption(target);
  }

  handleLinkKeydown(event) {
    if (event.key === "Enter") {
      const target = event.target.closest(".disclosure--option");
      if (!target) return;
      
      this.selectOption(target);
      this.toggle.focus();
    }
  }

  selectOption(target) {
    const value = target.dataset.value;
    const html = target.innerHTML;
    
    this.toggleFormDisplay(false);
    this.current_option.innerHTML = html;
    
    this.links.forEach(link => link.setAttribute("aria-current", false));
    target.setAttribute("aria-current", true);
    
    this.input.value = value;
    const changeEvent = new Event("change");
    this.input.dispatchEvent(changeEvent);
  }

  toggleDisclosure(open) {
    // Close all other disclosures
    Disclosure.instances.forEach(instance => {
      if (instance === this) {
        instance.toggleFormDisplay(open);
      } else {
        instance.toggleFormDisplay(false);
      }
    });
  }

  toggleFormDisplay(show) {
    this.toggle.setAttribute("aria-expanded", show);
    this.form.setAttribute("data-transition-active", show);
  }

  updateFormPosition() {
    // Calculate positions once to avoid layout thrashing
    this.toggle_pos = this.getBoundingClientRect();
    
    // Use performant way to get dimensions
    const rect = this.form.getBoundingClientRect();
    const formHeight = rect.height || 0;
    const formWidth = rect.width || 0;
    
    // Batch DOM reads and writes to avoid layout thrashing
    const viewportHeight = this.viewport ? this.viewport.offsetHeight : 0;
    const viewportWidth = this.viewport ? this.viewport.offsetWidth : 0;
    
    // Calculate positions
    const totalHeightBelow = formHeight + this.toggle_and_form_gap + this.window_and_form_gap;
    const toggleTop = this.toggle_pos.top;
    const spaceBelow = viewportHeight - toggleTop - this.toggle.offsetHeight;
    const spaceAbove = toggleTop;
    
    // Determine vertical position
    let topValue;
    if (spaceBelow < totalHeightBelow && spaceBelow < spaceAbove) {
      topValue = `-${formHeight + this.toggle_and_form_gap}px`;
    } else {
      topValue = `${this.toggle.offsetHeight + this.toggle_and_form_gap}px`;
    }
    
    // Determine horizontal position (only for non-option-picker types)
    if (this.type !== "option-picker") {
      const spaceRight = viewportWidth - this.toggle_pos.left;
      const totalWidthRight = formWidth + this.window_and_form_gap;
      
      const rightValue = spaceRight <= totalWidthRight ? 
        `${this.toggle.offsetWidth + this.window_and_form_gap - spaceRight}px` : 
        "unset";
      
      // Batch DOM writes
      this.form.style.right = rightValue;
    }
    
    // Set top position
    this.form.style.top = topValue;
  }

  optionChangeCallback() {
    this.input.addEventListener("change", (event) => {
      if (this.type === "url-redirect") {
        window.location.href = event.target.value;
      } else if (this.type === "localization") {
        event.target.closest("form").submit();
      }
    });
  }
}

// Register only if not already defined
if (!customElements.get("disclosure-root")) {
  customElements.define("disclosure-root", Disclosure);
}